import cx from 'clsx';
import React from 'react';
import { type Control, useController } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as AttachmentsFieldIcon } from '@assets/svg/attachments-field.svg';

import FilePreview from '@components/common/FilePreview/FilePreview';
import TextArea from '@components/form/TextArea/TextArea';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { IFile } from '@model/file';

import { useModalActions } from '@store/useModalStore';

import { type IWarrantyForm, WARRANTY_FIELDS } from '../../constants/schemaWarranty';
import style from './style.module.scss';

interface IProps {
  control: Control<IWarrantyForm>;
}

const FILE_SIZE_LIMIT = 1024 * 1024 * 10;
const FILES_COUNT_LIMIT = 10;

const StepGeneralDescription: React.FC<IProps> = ({ control }) => {
  const { t } = useTranslation();

  const { setSecondaryModal } = useModalActions();

  const {
    field: { value: attachmentsFieldValue, onChange },
  } = useController({ name: WARRANTY_FIELDS.ATTACHMENTS, control });

  const removeFile = (file: IFile) => {
    const filteredFilesList = attachmentsFieldValue.filter((item: IFile) => item.url !== file.url);

    onChange(filteredFilesList);
  };

  const showUploadModal = () => {
    setSecondaryModal({
      name: MODAL_NAMES.attachFilesModal,
      options: {
        name: WARRANTY_FIELDS.ATTACHMENTS,
        control,
        maxFileSize: FILE_SIZE_LIMIT,
        maxFilesCount: FILES_COUNT_LIMIT - attachmentsFieldValue.length,
      },
    });
  };

  return (
    <div className={style.stepGeneralDescriptionContainer}>
      <TextArea
        name={WARRANTY_FIELDS.DESCRIPTION}
        control={control}
        label={t('General Description of Problem or Issue with Product')}
        required
      />
      <div className={style.attachesContainer}>
        <div className={style.attaches}>
          <label className={style.attachmentsLabel}>
            {t('Upload pictures of the hardware being submitted')}
          </label>
          <div className={style.attachBtnRight} onClick={showUploadModal}>
            <AttachmentsFieldIcon className={style.uploadIconRight} />
            <span className={style.attachesLink}>{t('Click To Upload')} </span>
          </div>
        </div>

        <span className={style.uploadInstructionsText}>
          {t(
            'The pictures should show the entire device. For motes, remove the cap and include pictures of both the cap and the circuit board, showing the front and back. If you cannot upload them through this form, please attach the images to an email addressed to:',
          )}
          <a href="mailto:warranty@waites.net">warranty@waites.net</a>
        </span>
        <span className={style.uploadHintText}>
          {t(`Upload up to ${FILES_COUNT_LIMIT}  supported files (maximum 10 MB per file).`)}
        </span>
        {!attachmentsFieldValue.length && (
          <span className={style.uploadHintText}>{t('No attachments yet')}</span>
        )}
        {!!attachmentsFieldValue.length && (
          <div className={cx(style.attachesFiles, style.scrollingBlock, 'lightbox-block')}>
            {attachmentsFieldValue.map((file: IFile) => (
              <FilePreview key={uuidv4()} file={file} removeFile={(file) => removeFile(file)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StepGeneralDescription;
